import DEFAULT_I18N from "~/i18n"
import { POLL_QUESTION_TYPES } from "~/lib/constants"
import { numberToLetter as DEFAULT_NUMBER_TO_LETTER } from "~/lib/utils"

export class Question {
    constructor({ question, rightAnswer, numberToLetter, i18n }) {
        this.numberToLetter = numberToLetter || DEFAULT_NUMBER_TO_LETTER
        this.i18n = i18n || DEFAULT_I18N.global
        this.question = question
        this.rightAnswer = rightAnswer
    }

    setQuestion(question) {
        Object.assign(this.question, question)
    }

    setQuestionCode(code) {
        this.question.code = code
    }

    setRightAnswer(rightAnswer) {
        this.rightAnswer = rightAnswer
    }

    isScorable() {
        if (!POLL_QUESTION_TYPES.includes(this.question.code)) {
            throw new Error(`code ${this.question.code} is not supported`)
        }

        switch (this.question.code) {
            case "FreeTextOptional":
            case "FreeTextOpinion":
            case "Range":
                return false
            case "FreeTextManual":
            case "FreeTextEvaluable":
            case "RangeManual":
            case "TrueOrFalse":
            case "YesOrNo":
            case "MultipleChoice":
            case "MultipleChoiceManual":
            case "OneChoice":
            case "JoinArrows":
            case "Order":
                return true
            default:
                throw new Error(
                    `code ${this.question.code} has no defined scorable status`
                )
        }
    }

    getTip() {
        if (!POLL_QUESTION_TYPES.includes(this.question.code)) {
            throw new Error(`code ${this.question.code} is not supported`)
        }

        switch (this.question.code) {
            case "FreeTextManual":
            case "FreeTextEvaluable":
            case "RangeManual":
                return this.i18n.t("common.scorableAnswer")
            case "FreeTextOptional":
            case "FreeTextOpinion":
            case "TrueOrFalse":
            case "YesOrNo":
            case "Range":
                return ""
            case "MultipleChoice":
            case "MultipleChoiceManual":
                return this.i18n.t("common.multipleAnswer")
            case "OneChoice":
                return this.i18n.t("common.uniqueAnswer")
            case "JoinArrows":
                return this.i18n.t("common.sortTheRightColumn")
            case "Order":
                return this.i18n.t("common.sortTheList")

            default:
                throw new Error(`code ${this.question.code} has no defined tip`)
        }
    }

    getCookedRightAnswer() {
        if (!this.rightAnswer) {
            return null
        }

        if (!POLL_QUESTION_TYPES.includes(this.question.code)) {
            throw new Error(`code ${this.question.code} is not supported`)
        }

        switch (this.question.code) {
            case "FreeTextManual":
            case "FreeTextEvaluable":
            case "FreeTextOptional":
            case "MultipleChoiceManual":
            case "Range":
            case "RangeManual":
                return null

            case "JoinArrows":
                return this.getMatchCookedRightAnswer()

            case "Order":
                return this.getSortCookedRightAnswer()

            case "MultipleChoice":
                return this.getMultipleChoiceCookedRightAnswer()

            case "OneChoice":
                return this.getSingleChoiceCookedRightAnswer()

            case "TrueOrFalse":
            case "YesOrNo":
                return this.i18n.t(`common.${this.rightAnswer}`)

            default:
                throw new Error(
                    `questionType ${this.question.code} has no defined rightAnswer`
                )
        }
    }

    getMatchCookedRightAnswer() {
        const isOnlyText =
            itOnlyHasText(this.question.choices.left) &&
            itOnlyHasText(this.question.choices.right)
        let rightAnswer = []
        for (const answer of this.rightAnswer) {
            const indexLeft = this.question.choices.left.findIndex(
                op => op.text === answer.id
            )
            const indexRight = this.question.choices.right.findIndex(
                op => op.text === answer.value
            )
            const leftItem = isOnlyText
                ? this.question.choices.left[indexLeft].text
                : indexLeft + 1
            const rightItem = isOnlyText
                ? this.question.choices.right[indexRight].text
                : this.numberToLetter(indexRight)
            rightAnswer.push(
                `${leftItem} ${this.i18n.t("common.with")} ${rightItem}`
            )
        }
        return rightAnswer
    }

    getSortCookedRightAnswer() {
        const isOnlyText = itOnlyHasText(this.question.choices)
        const answer = this.rightAnswer
            .map(a => {
                const index = this.question.choices.findIndex(
                    c => c.text === a.value
                )
                a.letter = isOnlyText ? a.value : this.numberToLetter(index)
                return a
            })
            .sort((a, b) => {
                if (a.id > b.id) {
                    return 1
                } else if (a.id < b.id) {
                    return -1
                } else {
                    return 0
                }
            })
        return answer.map(a => a.letter)
    }

    getMultipleChoiceCookedRightAnswer() {
        const isOnlyText = itOnlyHasText(this.question.choices)
        if (isOnlyText) {
            return this.rightAnswer.filter(a => a.value).map(a => a.id)
        }
        const answer = this.rightAnswer.map((a, i) => {
            a.letter = this.numberToLetter(i)
            return a
        })
        return answer.filter(a => a.value).map(a => a.letter)
    }

    getSingleChoiceCookedRightAnswer() {
        const isOnlyText = itOnlyHasText(this.question.choices)
        if (isOnlyText) {
            return this.rightAnswer.find(a => a.value).id
        } else {
            const index = this.rightAnswer.findIndex(a => a.value)
            if (index === -1) return
            return this.numberToLetter(index)
        }
    }
}

/**
 * Returns if a question has automatic correction or not
 * @param {String} questionCode
 */
export function hasAutomaticCorrection(questionCode) {
    if (!POLL_QUESTION_TYPES.includes(questionCode)) {
        throw new Error(`code ${questionCode} is not supported`)
    }

    switch (questionCode) {
        case "OneChoice":
        case "MultipleChoice":
        case "Order":
        case "YesOrNo":
        case "TrueOrFalse":
        case "JoinArrows":
            return true
        case "MultipleChoiceManual":
        case "FreeTextOpinion":
        case "FreeTextManual":
        case "FreeTextEvaluable":
        case "Range":
        case "RangeManual":
            return false
        default:
            return false
    }
}

/**
 * It merges the arrays localAnswers and remoteAnswers overwriting the value stored
 * in remoteAnswers if there is any answer in localAnswers containing the same questionId
 * @param {array} remoteAnswers
 * @param {array} localAnswers
 */

export function mergeAnswers(remoteAnswers, localAnswers) {
    if (!remoteAnswers || !remoteAnswers.length) {
        throw new Error("Not defined remote answer")
    }

    if (!localAnswers || !localAnswers.length) {
        return remoteAnswers
    }

    return remoteAnswers.map(ra => {
        const localAnswer = localAnswers.find(
            la => la.questionId === ra.questionId
        )
        return localAnswer || ra
    })
}

/**
 * It checks if the array of choices has only text options
 * @param {array} choices
 */
export function itOnlyHasText(choices) {
    return choices.every(c => c.type == "text")
}

/**
 * It returns the assigned icon for each status
 * @param {string} status
 */
export function getIconFromStatus(status) {
    const STATUS_SUCCESS = "success"
    const STATUS_FAIL = "danger"
    const STATUS_PENDING_CORRECTION = "warning"
    const STATUS_INFO = "gray-dark"
    const ICON_SUCCESS = "radiobutton-check-line"
    const ICON_FAIL = "radiobutton-cross-line"
    const ICON_PENDING_CORRECTION = "stopwatch"
    const ICON_INFO = "info"

    switch (status) {
        case STATUS_SUCCESS:
            return ICON_SUCCESS
        case STATUS_FAIL:
            return ICON_FAIL
        case STATUS_PENDING_CORRECTION:
            return ICON_PENDING_CORRECTION
        case STATUS_INFO:
            return ICON_INFO
        default:
            throw new Error(`Status ${status} has no defined icon`)
    }
}
